import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/apiService';
import { authService } from '../../services/authService';
import { useNotification } from '../NotificationProvider';
import { FaClock, FaUser } from 'react-icons/fa';
import './TrainingNotes.scss';

const TrainingNotes = () => {
  const { trainingId } = useParams();
  const [notes, setNotes] = useState('');
  const [allNotes, setAllNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const { showSuccess, showError } = useNotification();
  
  const isCoach = authService.isManager() || sessionStorage.getItem('user_type') === 'coach';

  useEffect(() => {
    if (isCoach) {
      fetchNotes();
    }
  }, [trainingId, isCoach]);

  const fetchNotes = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get_training_notes.php?match_id=${trainingId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setAllNotes(response.data.notes);
        
        // coach_id is AUTH user_id (manager_id for coaches)
        const currentCoachId = parseInt(sessionStorage.getItem('user_id'), 10);
        const currentCoachNote = response.data.notes.find(note =>
          parseInt(note.coach_id, 10) === currentCoachId
        );
        
        if (currentCoachNote) {
          setNotes(currentCoachNote.notes);
          setLastSaved(new Date(currentCoachNote.updated_at));
        }
      }
    } catch (error) {
      console.error('Error fetching training notes:', error);
      if (error.response?.data?.message) {
        showError(error.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!notes.trim()) {
      showError('Please enter some notes before saving');
      return;
    }

    setIsSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await api.post(`${process.env.REACT_APP_API_URL}/save_training_notes.php`, {
        match_id: trainingId,
        notes: notes.trim()
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        showSuccess('Training notes saved successfully');
        setLastSaved(new Date());
        await fetchNotes(); // Refresh all notes
      } else {
        showError(response.data.message || 'Failed to save notes');
      }
    } catch (error) {
      console.error('Error saving training notes:', error);
      if (error.response?.data?.message) {
        showError(error.response.data.message);
      } else {
        showError('Failed to save training notes');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (note) => {
    setEditingNoteId(note.note_id);
    setEditNotes(note.notes);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNotes('');
  };

  const handleSaveEdit = async () => {
    if (!editNotes.trim()) {
      showError('Please enter some notes before saving');
      return;
    }

    setIsSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await api.post(`${process.env.REACT_APP_API_URL}/save_training_notes.php`, {
        match_id: trainingId,
        notes: editNotes.trim()
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        showSuccess('Training notes updated successfully');
        setEditingNoteId(null);
        setEditNotes('');
        await fetchNotes(); // Refresh all notes
      } else {
        showError(response.data.message || 'Failed to update notes');
      }
    } catch (error) {
      console.error('Error updating training notes:', error);
      if (error.response?.data?.message) {
        showError(error.response.data.message);
      } else {
        showError('Failed to update training notes');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    setIsSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await api.delete(`${process.env.REACT_APP_API_URL}/delete_training_notes.php`, {
        data: {
          note_id: noteId
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        showSuccess('Training note deleted successfully');
        await fetchNotes(); // Refresh all notes
      } else {
        showError(response.data.message || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting training note:', error);
      if (error.response?.data?.message) {
        showError(error.response.data.message);
      } else {
        showError('Failed to delete training note');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    // Get day with ordinal suffix
    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);
    
    // Get abbreviated month
    const month = date.toLocaleString('en-GB', { month: 'short' });
    
    // Get year
    const year = date.getFullYear();
    
    // Get time in 12-hour format
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    return `${day}${ordinalSuffix} ${month} ${year} - ${displayHours}:${displayMinutes}${ampm}`;
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  if (!isCoach) {
    return (
      <div className="training-notes">
        <div className="access-denied">
          <h3>Access Denied</h3>
          <p>Only coaches can view and edit training notes.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="training-notes">
        <div className="loading">Loading training notes...</div>
      </div>
    );
  }

  return (
    <div className="training-notes">
      <div className="notes-header">
        <h3>Training Notes</h3>
        {lastSaved && (
          <div className="last-saved">
            <FaClock />
            <span>Last saved: {formatDate(lastSaved)}</span>
          </div>
        )}
      </div>

      <div className="notes-editor">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter your training notes here... (supports paragraphs)"
          rows="10"
          className="notes-textarea"
        />
        <div className="save-section">
          <button 
            className="save-button"
            onClick={handleSave}
            disabled={isSaving}
          >
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="M433.941 129.941l-83.882-83.882A48 48 0 0 0 316.118 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V163.882a48 48 0 0 0-14.059-33.941zM224 416c-35.346 0-64-28.654-64-64 0-35.346 28.654-64 64-64s64 28.654 64 64c0 35.346-28.654 64-64 64zm96-304.52V212c0 6.627-5.373 12-12 12H76c-6.627 0-12-5.373-12-12V108c0-6.627 5.373-12 12-12h228.52c3.183 0 6.235 1.264 8.485 3.514l3.515 3.515c2.25 2.25 3.514 5.302 3.514 8.485z"></path>
            </svg>
            {isSaving ? 'Saving...' : 'Save Notes'}
          </button>
        </div>
      </div>

      {allNotes.length > 0 && (
        <div className="all-notes">
          <h4>All Coach Notes</h4>
          <div className="notes-list">
            {allNotes.map((note) => (
              <div key={note.note_id} className="note-item">
                <div className="note-header">
                  <div className="coach-info">
                    <FaUser />
                    <span>{note.first_name} {note.last_name}</span>
                  </div>
                  <div className="note-date">
                    <FaClock />
                    <span>{formatDate(note.updated_at)}</span>
                  </div>
                </div>
                
                <div className="note-actions">
                  {editingNoteId !== note.note_id && (
                    <>
                      <button 
                        className="edit-button"
                        onClick={() => handleEdit(note)}
                        title="Edit note"
                      >
                        <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                          <path d="M497.9 142.1l-46.1 46.1c-4.7 4.7-12.3 4.7-17 0l-111-111c-4.7-4.7-4.7-12.3 0-17l46.1-46.1c18.7-18.7 49.1-18.7 67.9 0l60.1 60.1c18.8 18.7 18.8 49.1 0 67.9zM284.2 99.8L21.6 362.4.4 483.9c-2.9 16.4 11.4 30.6 27.8 27.8l121.5-21.3 262.6-262.6c4.7-4.7 4.7-12.3 0-17l-111-111c-4.8-4.7-12.4-4.7-17.1 0zM124.1 339.9c-5.5-5.5-5.5-14.3 0-19.8l154-154c5.5-5.5 14.3-5.5 19.8 0s5.5 14.3 0 19.8l-154 154c-5.5 5.5-14.3 5.5-19.8 0zM88 424h48v36.3l-64.5 11.3-31.1-31.1L51.7 376H88v48z"></path>
                        </svg>
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDelete(note.note_id)}
                        disabled={isSaving}
                        title="Delete note"
                      >
                        <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                          <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"></path>
                        </svg>
                      </button>
                    </>
                  )}
                </div>
                <div className="note-content">
                  {editingNoteId === note.note_id ? (
                    <>
                      <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Edit your training notes..."
                        rows="8"
                        className="edit-notes-textarea"
                      />
                      <div className="edit-actions">
                        <button 
                          className="save-edit-button"
                          onClick={handleSaveEdit}
                          disabled={isSaving}
                        >
                          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                            <path d="M433.941 129.941l-83.882-83.882A48 48 0 0 0 316.118 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V163.882a48 48 0 0 0-14.059-33.941zM224 416c-35.346 0-64-28.654-64-64 0-35.346 28.654-64 64-64s64 28.654 64 64c0 35.346-28.654 64-64 64zm96-304.52V212c0 6.627-5.373 12-12 12H76c-6.627 0-12-5.373-12-12V108c0-6.627 5.373-12 12-12h228.52c3.183 0 6.235 1.264 8.485 3.514l3.515 3.515c2.25 2.25 3.514 5.302 3.514 8.485z"></path>
                          </svg>
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button 
                          className="cancel-edit-button"
                          onClick={handleCancelEdit}
                          disabled={isSaving}
                        >
                          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 352 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                            <path d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"></path>
                          </svg>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    note.notes.split('\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingNotes; 